import { Payment_Review_Records_View_Order_By } from "@/api/hasura/generated/graphql"
import { PaymentReviewRecordParser } from "@/models/operations/paymentReviewRecord/paymentReviewRecord.parser"
import {
  IPaymentReviewRecord,
  PaymentReviewFailureErrorCode,
  PaymentReviewRecordStatus,
} from "@/models/operations/paymentReviewRecord/paymentReviewRecord.types"
import { PaymentReviewRecordCalcParser } from "@/models/operations/paymentReviewRecordCalc/paymentReviewRecordCalc.parser"
import { PaymentReviewSummaryParser } from "@/models/operations/paymentReviewSummary/paymentReviewSummary.parser"
import { IPaymentReviewSummary } from "@/models/operations/paymentReviewSummary/paymentReviewSummary.types"
import { IPaymentReviewId } from "@/models/operations/paymentReviewId/paymentReviewId.types"
import { PaymentReviewIdParser } from "@/models/operations/paymentReviewId/paymentReviewId.parser"
import { Dayjs } from "dayjs"
import { flipOrder, nullsFirstInOrder } from "@/api/utils/hasuraHelpers"
import { HasuraQueries } from "@/api/hasura/queries"
import { useQuery } from "@/api/utils/useQuery"
import { QueryResult } from "@/api/utils/types"
import { PaymentReviewSummary } from "@/models/operations/paymentReviewSummary/paymentReviewSummary"
import { IPaymentReviewRecordCalc } from "@/models/operations/paymentReviewRecordCalc/paymentReviewRecordCalc.types"

export type RetryOptions = {
  overrideIncreaseThreshold: boolean
  ignoreAccountBalance: boolean
}

export type PaymentReviewRecordFilters = {
  status?: PaymentReviewRecordStatus
  errorCodes?: PaymentReviewFailureErrorCode[]
  from?: Dayjs
  to?: Dayjs
  nextPaymentDateFrom?: Dayjs
  nextPaymentDateTo?: Dayjs
  changedAmountMin?: number
  changedAmountMax?: number
  lastRetryDateFrom?: Dayjs
  lastRetryDateTo?: Dayjs
}

export type RecordsOrderBy = Payment_Review_Records_View_Order_By
export type RecordsQueryResult =
  | {
      records: IPaymentReviewRecord[]
      totalCount: number
      activeFailureCodes: PaymentReviewFailureErrorCode[] | null
    }
  | undefined

class PaymentReviewServiceClass {
  getLatestPaymentReviewId(): QueryResult<number | undefined> {
    return useQuery({
      ...HasuraQueries.paymentReviews.latestId,
      placeholderData: null,
      useRawResponse: true,
      parserFn: (data) => {
        return data.payment_reviews_aggregate.aggregate?.max?.id
      },
    })
  }

  getPaymentReviewIds(): QueryResult<IPaymentReviewId[]> {
    return useQuery({
      ...HasuraQueries.paymentReviews.paymentReviewIds,
      placeholderData: [] as IPaymentReviewId[],
      useRawResponse: true,
      parserFn: (data) => {
        const latestManualId = data.payment_reviews
          .filter((data) => data.type === "manual")
          .slice(0, 1)

        const latestAutomatedIds = data.payment_reviews
          .filter((data) => data.type === "automated")
          .slice(0, 3)

        const filteredPaymentReviewIds = latestManualId
          .concat(latestAutomatedIds)
          .map((data) => {
            return PaymentReviewIdParser.toDomain(data)
          })

        return filteredPaymentReviewIds
      },
    })
  }

  getSummary(
    paymentReviewId: number,
  ): QueryResult<IPaymentReviewSummary | undefined> {
    return useQuery({
      ...HasuraQueries.paymentReviews.summary(paymentReviewId),
      placeholderData: new PaymentReviewSummary(0, 0, 0, 0),
      useRawResponse: true,
      parserFn: (data) => {
        if (!data) return undefined
        return PaymentReviewSummaryParser.toDomain(data)
      },
    })
  }

  getRecords(
    paymentReviewId: number,
    filters: PaymentReviewRecordFilters,
    page: number,
    pageSize: number,
    orderBy: RecordsOrderBy,
  ): QueryResult<RecordsQueryResult> {
    orderBy = nullsFirstInOrder(orderBy)
    orderBy = flipOrder(orderBy, "account_balance")

    return useQuery({
      ...HasuraQueries.paymentReviews.records({
        paymentReviewId,
        limit: pageSize,
        offset: (page - 1) * pageSize,
        orderBy,
        status: { _eq: filters.status },
        failureErrorCode: {
          _in: filters.errorCodes?.length ? filters.errorCodes : undefined,
        },
        createdAt: {
          _gte: filters.from,
          _lte: filters.to,
        },
        nextPaymentDate: {
          _gte: filters.nextPaymentDateFrom,
          _lte: filters.nextPaymentDateTo,
        },
        changedAmount: {
          _gte: filters.changedAmountMin,
          _lte: filters.changedAmountMax,
        },
        lastRetryDate: {
          _gte: filters.lastRetryDateFrom,
          _lte: filters.lastRetryDateTo,
        },
      }),
      placeholderData: {
        records: [],
        totalCount: 0,
        activeFailureCodes: [] as PaymentReviewFailureErrorCode[],
      },
      useRawResponse: true,
      disableCache: true,
      parserFn: (data) => {
        if (!data) return undefined
        return {
          records: data.payment_review_records_view.map((prr) =>
            PaymentReviewRecordParser.toDomain(prr),
          ),
          totalCount:
            data.payment_review_records_view_aggregate.aggregate?.count ?? 0,
          activeFailureCodes: data.active_failure_reasons?.reduce(
            (acc, { failure_error_code }) => {
              if (failure_error_code) {
                acc.push(failure_error_code as PaymentReviewFailureErrorCode)
              }
              return acc
            },
            [] as PaymentReviewFailureErrorCode[],
          ),
        }
      },
    })
  }

  getRecord(
    paymentReviewId: number,
  ): QueryResult<IPaymentReviewRecordCalc | undefined> {
    return useQuery({
      ...HasuraQueries.paymentReviews.record(paymentReviewId),
      useRawResponse: true,
      parserFn: (data) => {
        if (!data) return undefined
        return PaymentReviewRecordCalcParser.toDomain(data)
      },
    })
  }
}

export const PaymentReviewService = new PaymentReviewServiceClass()
